// src/components/LoginModal/LoginModal.js

import React, { useState, useContext } from 'react';
import './LoginModal.css';
import UserContext from '../../contexts/UserContext';

const LoginModal = ({ onClose }) => {
  const { setUser, login, signup, forgotPassword } = useContext(UserContext);

  const [isSignup, setIsSignup] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [infoMessage, setInfoMessage] = useState('');

  const toggleMode = () => {
    setIsSignup(!isSignup);
    setError('');
    setFormData({ email: '', password: '', confirmPassword: '', name: '' });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  // Show forgot-password form
  const handleForgotClick = () => {
    setError('');
    setInfoMessage('');
    setIsForgotPassword(true);
    setFormData({ email: '', password: '', confirmPassword: '', name: '' });
  };

  // Back to login/signup view
  const handleBackToLogin = () => {
    setError('');
    setInfoMessage('');
    setIsForgotPassword(false);
    setFormData({ email: '', password: '', confirmPassword: '', name: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const { email, password, confirmPassword, name } = formData;

    if (isSignup) {
      // Signup Validation
      if (!email || !password || !confirmPassword || !name) {
        setError('Please fill in all required fields.');
        setLoading(false);
        return;
      }
      if (password !== confirmPassword) {
        setError('Passwords do not match.');
        setLoading(false);
        return;
      }

      try {
        const data = await signup(formData);
        setUser(data.user);
        onClose();
      } catch (err) {
        setError(err.error || 'Signup failed.');
      } finally {
        setLoading(false);
      }
    } else {
      // Login Validation
      if (!email || !password) {
        setError('Please enter both email and password.');
        setLoading(false);
        return;
      }

      try {
        const data = await login(formData);
        setUser(data.user);
        onClose();
      } catch (err) {
        setError(err.error || 'Login failed.');
      } finally {
        setLoading(false);
      }
    }
  };

  // Handle forgot-password submission
  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setInfoMessage('');
    setLoading(true);

    const { email } = formData;
    if (!email) {
      setError('Please enter your email.');
      setLoading(false);
      return;
    }

    try {
      const data = await forgotPassword(email);
      setInfoMessage(data.message);
    } catch (err) {
      setError(err?.error || err?.message || 'Error sending reset link.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="login-modal">
        {isForgotPassword ? (
          // Forgot Password Form
          <>
            <h2>Reset Password</h2>
            {error && <div className="error-message">{error}</div>}
            {infoMessage && <div className="info-message">{infoMessage}</div>}
            <form onSubmit={handleForgotSubmit}>
              <label htmlFor="email">Email</label>
              <input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter your email"
                required
              />
              <div className="modal-buttons">
                <button type="submit" disabled={loading}>
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </button>
                <button
                  type="button"
                  onClick={handleBackToLogin}
                  disabled={loading}
                >
                  Back to Login
                </button>
              </div>
            </form>
          </>
        ) : (
          // Login / Sign Up Form
          <>
            <h2>{isSignup ? 'Sign Up' : 'Login'}</h2>
            {error && <div className="error-message">{error}</div>}
            <form onSubmit={handleSubmit}>
              {isSignup && (
                <>
                  <label htmlFor="name">Name</label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter your name"
                    required
                  />
                </>
              )}
              <label htmlFor="email">Email</label>
              <input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter your email"
                required
              />
              <label htmlFor="password">Password</label>
              <input
                id="password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Enter your password"
                required
              />

              {isSignup && (
                <>
                  <label htmlFor="confirmPassword">Confirm Password</label>
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder="Confirm your password"
                    required
                  />
                </>
              )}

              <div className="modal-buttons">
                <button type="submit" disabled={loading}>
                  {loading ? 'Submitting...' : isSignup ? 'Sign Up' : 'Login'}
                </button>
                <button type="button" onClick={onClose} disabled={loading}>
                  Cancel
                </button>
              </div>
            </form>

            <div className="toggle-auth">
              {isSignup ? (
                <p>
                  Already have an account?{' '}
                  <button
                    type="button"
                    onClick={toggleMode}
                    className="toggle-button"
                  >
                    Login
                  </button>
                </p>
              ) : (
                <p>
                  Don't have an account?{' '}
                  <button
                    type="button"
                    onClick={toggleMode}
                    className="toggle-button"
                  >
                    Sign Up
                  </button>
                </p>
              )}
              {/* [2] Moved “Forgot Password?” link below signup toggle */}
              {!isSignup && (
                <p className="forgot-password-bottom">
                  <button
                    type="button"
                    className="forgot-link"
                    onClick={handleForgotClick}
                  >
                    Forgot Password?
                  </button>
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default LoginModal;
